import clsx from 'clsx';
import { ReactNode, forwardRef } from 'react';

import styles from './Badge.module.css';

export type BadgeVariant = 'manual' | 'simple';

export type BadgeColor =
  | 'gray'
  | 'green'
  | 'red'
  | 'orange'
  | 'yellow'
  | 'blue'
  | 'indigo'
  | 'steel'
  | 'pink'
  | 'error'
  | 'success'
  | 'purple-gradient';

export interface BadgeProps extends Omit<
  React.ComponentPropsWithoutRef<'span'>,
  'color'
> {
  /** Variant: 'manual' for larger icon+text tags, 'simple' for smaller dot+text tags */
  variant?: BadgeVariant;
  /** Color variant */
  color?: BadgeColor;
  /** Left section: icon for manual variant, dot element for simple variant */
  leftSection?: ReactNode;
  /** Text content */
  children: ReactNode;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'manual',
      color = 'gray',
      leftSection,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <span
        ref={ref}
        data-variant={variant}
        data-color={color}
        {...props}
        className={clsx(styles.root, className)}
      >
        {leftSection && (
          <span className={styles.leftSection}>{leftSection}</span>
        )}
        <span className={styles.label}>{children}</span>
      </span>
    );
  }
);

Badge.displayName = 'Badge';
